import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { PLAN_SETTINGS } from '../../common/constants/plan_settings';
import FeatureList from './FeatureList';
import { selectUser } from '../../slices/user/userSlice';
import MessageModal from '../../common/message-modal/MessageModal';
import Checkbox from '@mui/material/Checkbox';
import { Box } from '@mui/material';
import { NWClient } from '../../client/NWClient';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { COUPONS } from '../../common/constants/coupons';
import { sanitizeRedirectUrl } from '../../common/helpers/sanitize';

type Plan =
  | 'free'
  | 'essentials'
  | 'growth'
  | 'apa_discounted_essentials'
  | 'free_essentials'
  | 'free_growth'
  | 'esign_only';

interface PricingCardProps {
  termsAndConditionsAgreed: boolean;
  plan: Plan;
  billingPeriod: 'monthly' | 'annual';
  discount_code?: string;
  paymentLinks: {
    [key: string]: string;
  };
}

const ModalMessageContent = (props: {
  userAgreed: boolean;
  setUserAgreed: React.SetStateAction<any>;
  plan: Plan;
}) => {
  const { t } = useTranslation();
  const { userAgreed, setUserAgreed, plan } = props;
  return (
    <>
      <h2>{t('subscriptionAgreementTitle')}</h2>
      <Box className='checkbox-row' style={{ display: 'flex', alignItems: 'flex-start' }}>
        <Checkbox
          style={{ marginLeft: -10, paddingTop: 0, paddingBottom: 0 }}
          id='subscriptionAgreement'
          checked={userAgreed}
          onChange={(event) => {
            setUserAgreed(event.target.checked);
          }}
        />
        <label style={{ marginTop: 0 }} htmlFor='subscriptionAgreement'>
          {t('subscriptionAgreementTextPartOne')}
          <a
            href='https://www.businesskitz.com/terms-and-conditions'
            target='_blank'
            rel='noreferrer'
          >
            {t('businessKitzTermsAndConditions')}
          </a>
          {plan === 'esign_only'
            ? t('subscriptionAgreementTextPartTwoEsignOnly')
            : t('subscriptionAgreementTextPartTwo')}
        </label>
      </Box>
    </>
  );
};

const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  billingPeriod,
  discount_code,
  paymentLinks,
  termsAndConditionsAgreed,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectAllCompanies)[0];
  const currentUser = useAppSelector(selectUser);
  const paymentLinkParams = `?prefilled_email=${currentUser?.email}&client_reference_id=${currentUser?.user_uuid}`;
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  // featured Plans = essentials plans
  const featuredPlansList = ['essentials', 'apa_discounted_essentials', 'free_essentials'];

  // Growth plans

  const growthPlansList = ['growth', 'free_growth'];

  const esignOnlyPlanList = ['esign_only'];

  const isFeatured = featuredPlansList.includes(plan);
  const planKey = `pricingPlans.${plan}`;

  const [agreementMessageVisible, setAgreementMessageVisible] = useState(false);
  const [userAgreed, setUserAgreed] = useState(false);
  const couponPrefill = discount_code ? `&prefilled_promo_code=${discount_code}` : '';

  const getPrice = () => {
    if (
      COUPONS.OFF_50_COUPONS.includes(discount_code) &&
      COUPONS.ESSENTIALS_COUPONS.includes(discount_code) &&
      (plan === 'essentials' || plan === 'free_essentials')
    ) {
      return 'A$59.50';
    } else if (
      COUPONS.OFF_25_COUPONS.includes(discount_code) &&
      COUPONS.ESSENTIALS_COUPONS.includes(discount_code) &&
      (plan === 'essentials' || plan === 'free_essentials')
    ) {
      return 'A$89.25';
    } else if (
      COUPONS.OFF_35_WITH_TAX_COUPONS.includes(discount_code) &&
      COUPONS.ESSENTIALS_COUPONS.includes(discount_code) &&
      (plan === 'essentials' || plan === 'free_essentials')
    ) {
      return 'A$85.09';
    } else if (
      COUPONS.OFF_35_WITH_TAX_COUPONS.includes(discount_code) &&
      COUPONS.GROWTH_COUPONS.includes(discount_code) &&
      (plan === 'growth' || plan === 'free_growth')
    ) {
      return 'A$170.89';
    } else if (
      COUPONS.OFF_35_WITH_TAX_COUPONS.includes(discount_code) &&
      COUPONS.ESIGN_ONLY_COUPONS.includes(discount_code) &&
      plan === 'esign_only'
    ) {
      return 'A$20.74';
    } else if (
      COUPONS.OFF_50_COUPONS.includes(discount_code) &&
      COUPONS.GROWTH_COUPONS.includes(discount_code) &&
      (plan === 'growth' || plan === 'free_growth')
    ) {
      return 'A$119.50';
    } else if (COUPONS.OFF_100_COUPONS.includes(discount_code)) {
      return 'A$0';
    }
    if (billingPeriod === 'annual') {
      return t(`${planKey}.priceAnnual`, { defaultValue: t(`${planKey}.price`) });
    }
    return t(`${planKey}.price`);
  };

  const getBillingPeriodText = () => {
    if (billingPeriod === 'annual') {
      return t(`${planKey}.periodAnnual`, { defaultValue: t(`${planKey}.period`) });
    }
    return t(`${planKey}.period`);
  };

  const getCtaClass = () => {
    if (featuredPlansList.includes(plan)) {
      return 'successCta';
    }
    switch (plan) {
      case 'free':
        return 'primaryCta';
      case 'esign_only':
        return 'outlineCta';
      default:
        return 'secondaryCta';
    }
  };

  const getPlanId = () => {
    if (featuredPlansList.includes(plan)) {
      return [PLAN_SETTINGS.BUSINESS_ESSENTIALS_ID, PLAN_SETTINGS.BUSINESS_ESSENTIALS_ANNUAL_ID];
    } else if (growthPlansList.includes(plan)) {
      return [PLAN_SETTINGS.BUSINESS_GROWTH_ID, PLAN_SETTINGS.BUSINESS_GROWTH_ANNUAL_ID];
    } else if (esignOnlyPlanList.includes(plan)) {
      return [PLAN_SETTINGS.ESIGN_ONLY_ID, PLAN_SETTINGS.ESIGN_ONLY_ANNUAL_ID];
    } else {
      return null;
    }
  };

  const isCurrentPlan = () => {
    if (plan === 'free') {
      return !company?.stripe_product_id;
    }
    const planIds = getPlanId();
    return planIds && planIds.includes(company?.stripe_product_id);
  };

  const isGrowthOrPremium = () => {
    return (
      company?.stripe_product_id === PLAN_SETTINGS.BUSINESS_GROWTH_ID ||
      company?.stripe_product_id === PLAN_SETTINGS.PREMIUM_ID
    );
  };

  const shouldDisableButton = () => {
    if (plan === 'free') {
      return true;
    }
    if (plan === 'growth') {
      return isCurrentPlan() || isGrowthOrPremium();
    }
    return isCurrentPlan();
  };

  const getButtonText = () => {
    if (plan === 'free') {
      return t(`${planKey}.cta`);
    }
    if (shouldDisableButton()) {
      return t('subscriptionPlanControls.started');
    }
    return t(`${planKey}.cta`);
  };

  // Determine how many features should be bold based on plan
  const getBoldCount = () => {
    if (featuredPlansList.includes(plan)) {
      return 4;
    } else if (growthPlansList.includes(plan)) {
      return 3;
    } else {
      return 0;
    }
  };

  const getFeaturesTitle = () => {
    if (featuredPlansList.includes(plan)) {
      return `Everything in ${t('pricingPlans.free.name')}, plus:`;
    } else if (growthPlansList.includes(plan)) {
      return `Everything in ${t('pricingPlans.essentials.name')}, plus:`;
    } else if (plan === 'esign_only') {
      return `Everything in ${t('pricingPlans.free.name')}, plus:`;
    } else {
      return 'Key Features:';
    }
  };

  const getSignOnFeeText = () => {
    switch (plan) {
      case 'essentials':
        return t('pricingPlans.essentials.signOnFee');
      case 'growth':
        return t('pricingPlans.growth.signOnFee');
      default:
        return null;
    }
  };

  return (
    <>
      <div className={`priceCard ${isFeatured ? 'featuredCard' : ''}`}>
        {isFeatured && <span className='popularBadge'>{t(`${planKey}.badge`)}</span>}
        <h2 className='planName'>{t(`${planKey}.name`)}</h2>
        <div className='alignDesc'>
          <p className='planTagline'>{t(`${planKey}.tagline`)}</p>
        </div>

        <div className='priceSection alignPrice'>
          <span className='price'>{getPrice()}</span>
          <span className='billingPeriod'>
            {getBillingPeriodText()}
            {discount_code === 'ESSA35' &&
            (plan === 'essentials' || plan === 'growth' || plan === 'esign_only')
              ? ' (' + t('planPageMiscellaneous.includingGST') + ')'
              : ''}
          </span>
          {getSignOnFeeText() && <div className='signOnFeeText'>{getSignOnFeeText()}</div>}
        </div>

        <div className='ctaSection'>
          {shouldDisableButton() ? (
            <button disabled className={`ctaButton ${getCtaClass()}`}>
              {getButtonText()}
            </button>
          ) : (
            <>
              {!termsAndConditionsAgreed ? (
                <button
                  className={`ctaButton ${getCtaClass()}`}
                  onClick={
                    company
                      ? () => setAgreementMessageVisible(true)
                      : () =>
                          navigate(
                            `${ROUTES.COMPANY_CREATE}?backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
                          )
                  }
                >
                  {getButtonText()}
                </button>
              ) : (
                <a
                  href={
                    company
                      ? paymentLinks[plan] + paymentLinkParams + couponPrefill
                      : `${ROUTES.COMPANY_CREATE}?backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
                  }
                  className={`ctaButton ${getCtaClass()}`}
                  rel='noreferrer'
                >
                  {getButtonText()}
                </a>
              )}
            </>
          )}
          {(plan === 'free' || plan === 'esign_only') && (
            <p className='ctaSubtext'>{t(`${planKey}.ctaSubtext`)}</p>
          )}
          {plan !== 'free' && plan !== 'esign_only' && billingPeriod === 'monthly' && (
            <p className='ctaSubtext'>{t('contractPeriod')}</p>
          )}
        </div>

        <div className='featuresSection'>
          <div className='alignHead'>
            <h3 className='featuresTitle'>{getFeaturesTitle()}</h3>
          </div>
          <div className='featureListWrapper'>
            <FeatureList
              features={t(`${planKey}.features`, { returnObjects: true }) as string[]}
              unavailable={t(`${planKey}.unavailable`, { returnObjects: true }) as string[]}
              boldCount={getBoldCount()}
            />
          </div>
        </div>
        {agreementMessageVisible && (
          <MessageModal
            open={agreementMessageVisible}
            modalContent={
              <ModalMessageContent
                plan={plan}
                userAgreed={userAgreed}
                setUserAgreed={setUserAgreed}
              />
            }
            actionButtonText={t('buttons.continue')}
            actionButtonDisabled={!userAgreed}
            actionButtonFn={() => {
              if (!termsAndConditionsAgreed) {
                dispatch(showOverlay());
                NWClient.subscriptionTermsAccept()
                  .then(() => {
                    window.open(paymentLinks[plan] + paymentLinkParams + couponPrefill, '_self');
                    setAgreementMessageVisible(false);
                  })
                  .catch(() => {
                    toast.error(t('messages.errorOccurred'));
                  })
                  .finally(() => {
                    dispatch(hideOverlay());
                  });
              } else {
                window.open(paymentLinks[plan] + paymentLinkParams + couponPrefill, '_self');
              }
            }}
            goBackButtonText={t('buttons.goBack')}
            onClose={() => {
              setAgreementMessageVisible(false);
            }}
          />
        )}
      </div>
    </>
  );
};

export default PricingCard;
